/**
 * Every client-side route in Cosmos, in one place.
 *
 * Cosmos is a multi-module platform, so each module owns a URL prefix and its
 * pages live underneath it (`/psa/...`, and later `/rmm/...`, `/cipp/...`).
 * That keeps modules from colliding — PSA, RMM and Backup will all eventually
 * want a "search" and an "alerts" page.
 *
 * Import from here instead of writing path literals. Route strings were spread
 * across 21 files before this module existed; going through `ROUTES` means the
 * next structural change is a one-file edit.
 */

export const ROUTES = {
  login: '/login',
  /** Home — the navigational landing page (all modules). */
  home: '/',

  /** PSA module — Autotask. The only module with real functionality today. */
  psa: {
    root: '/psa',
    /** Both dashboards are Autotask-sourced, so they live in the PSA module. */
    dashboards: {
      root: '/psa/dashboards',
      serviceDesk: '/psa/dashboards/service-desk',
      sales: '/psa/dashboards/sales',
    },
    tickets: {
      mine: '/psa/tickets/mine',
      queues: '/psa/tickets/queues',
      search: '/psa/tickets/search',
      new: '/psa/tickets/new',
    },
    opportunities: {
      all: '/psa/opportunities',
      mine: '/psa/opportunities/mine',
      search: '/psa/opportunities/search',
      new: '/psa/opportunities/new',
    },
    /** Projects (dev/planning/Projects.md) — Autotask projects, phases and tasks. */
    projects: {
      all: '/psa/projects',
      mine: '/psa/projects/mine',
      myTasks: '/psa/projects/tasks/mine',
    },
    /** Combined tickets+opportunities search — the mobile PWA's Search tab. */
    search: '/psa/search',
  },

  /** RMM module — Datto RMM. */
  rmm: {
    root: '/rmm',
    dashboards: {
      overview: '/rmm/dashboards/overview',
      patching: '/rmm/dashboards/patching',
    },
    devices: '/rmm/devices',
    alerts: '/rmm/alerts',
    sites: '/rmm/sites',
  },

  /** CIPP module — Microsoft 365 tenants via CIPP. */
  cipp: {
    root: '/cipp',
    tenants: '/cipp/tenants',
    devices: '/cipp/devices',
    dashboards: {
      security: '/cipp/dashboards/security',
    },
  },

  /** CSP module — Microsoft NCE licence renewals + ordering (ported from NCEasy). */
  csp: {
    root: '/csp',
    dashboard: '/csp/dashboard',
    customers: '/csp/customers',
    licences: '/csp/licences',
    products: '/csp/products',
    requests: '/csp/requests',
    upload: '/csp/upload',
    mappings: '/csp/mappings',
  },

  /** Network — Sophos firewalls + UniFi (dev/planning/Networking.md). */
  network: {
    root: '/network',
    /** Sophos and UniFi are separate areas with their own data — never combined. */
    sophos: {
      dashboard: '/network/sophos',
      firewalls: '/network/sophos/firewalls',
      subscriptions: '/network/sophos/subscriptions',
      alerts: '/network/sophos/alerts',
      tenants: '/network/sophos/tenants',
    },
    unifi: {
      dashboard: '/network/unifi',
      sites: '/network/unifi/sites',
      consoles: '/network/unifi/consoles',
      devices: '/network/unifi/devices',
      wan: '/network/unifi/wan',
    },
  },

  /** Domains — dmarcian + CIPP Domain Analyser (dev/planning/dmarcian.md). */
  domains: {
    root: '/domains',
  },

  /** Insights — the cross-module layer (dev/planning/general.md). */
  insights: {
    root: '/insights',
    coverage: '/insights/coverage',
    gaps: '/insights/gaps',
    /** Record search results (devices, people, tickets, companies). */
    find: '/insights/find',
  },
  /** Companies — one page per Autotask company across every module (Orbit). */
  companies: '/companies',
  /** SOC module — Huntress (managed EDR / ITDR / SIEM). Flat pages by decision (2026-10-14). */
  soc: {
    root: '/soc',
    incidents: '/soc/incidents',
    agents: '/soc/agents',
    identities: '/soc/identities',
    organizations: '/soc/organizations',
  },
  /** Custom dashboards (cards from any module, saved + shared). Not PSA's /psa/dashboards. */
  dashboards: '/dashboards',
  /** AI — full-screen chat with the Foundry agent (Synfo MCP: Autotask, Datto RMM, IT Glue). */
  ai: '/ai',

  /** Docs — IT Glue (dev/planning/ITGlue.md). Read-only; passwords are never shown. */
  docs: {
    root: '/docs',
    organizations: '/docs/organizations',
    configurations: '/docs/configurations',
    expirations: '/docs/expirations',
  },

  /** Backup module — Datto SaaS Protection now; BCDR later under /backup/bcdr. */
  backup: {
    root: '/backup',
    saas: '/backup/saas',
  },

  admin: {
    root: '/admin',
    users: '/admin/users',
    webhook: '/admin/webhook',
    aiAssistant: '/admin/ai-assistant',
    system: '/admin/system',
    /** Status of every external system (config, live test, caches). */
    integrations: '/admin/integrations',
    /** Every product's records ↔ Autotask companies in one place. */
    mappings: '/admin/mappings',
    /** Per-module settings pages (server/moduleSettings.ts registry). */
    psa: '/admin/psa',
    /** The automatic project progress-status log (dry run / live writes). */
    projectStatus: '/admin/psa/project-status',
    rmm: '/admin/rmm',
    cipp: '/admin/cipp',
    cippSettings: '/admin/cipp/settings',
    csp: '/admin/csp',
    backup: '/admin/backup',
    insights: '/admin/insights',
    soc: '/admin/soc',
    ai: '/admin/ai',
    network: '/admin/network',
    networkSettings: '/admin/network/settings',
    domains: '/admin/domains',
  },

  account: {
    root: '/account',
    info: '/account/info',
    noteTemplates: '/account/note-templates',
    bookmarks: '/account/bookmarks',
  },
} as const;

/** Record detail paths. */
export const ticketPath = (id: number | string) => `/psa/tickets/${id}`;
export const oppPath = (id: number | string) => `/psa/opportunities/${id}`;
export const projectPath = (id: number | string) => `/psa/projects/${id}`;
/** A project with one task open in the drawer. */
export const projectTaskPath = (projectId: number | string, taskId: number | string) => `/psa/projects/${projectId}/tasks/${taskId}`;
/** Projects list pre-filtered (`show` = one of the page's filter ids), read once then dropped. */
export const psaProjectsView = (params: Record<string, string>) => `${ROUTES.psa.projects.all}?${new URLSearchParams(params)}`;
/** Datto RMM devices are keyed by their uid (a GUID), not the numeric id. */
export const rmmDevicePath = (uid: string) => `/rmm/devices/${uid}`;
/** The Sites page with a site selected (also Datto uids). */
export const rmmSitePath = (uid: string) => `/rmm/sites/${uid}`;
/** The Sites page with a whole client selected (every device across its sites), by Autotask company id. */
export const rmmClientSitesPath = (companyId: number) => `/rmm/sites/client/${companyId}`;
/** A CIPP tenant, keyed by its Entra tenant id (`customerId`). */
export const cippTenantPath = (customerId: string) => `/cipp/tenants/${customerId}`;
/** Intune devices, optionally pre-filtered (read once by the page's `filtersFromSearch`, then dropped). */
export const cippDevicesView = (params: Record<string, string>) => `${ROUTES.cipp.devices}?${new URLSearchParams(params)}`;

/** A Datto SaaS Protection customer (keyed by Datto's numeric saasCustomerId). */
export const backupSaasCustomerPath = (id: number | string) => `/backup/saas/${id}`;
/** SaaS customers pre-filtered (`show`: attention | unmapped | waste | gaps), read once then dropped. */
export const backupSaasView = (params: Record<string, string>) => `${ROUTES.backup.saas}?${new URLSearchParams(params)}`;

/** A Sophos/UniFi list page pre-filtered (`show` = the page's filter id), read once then dropped. */
export const networkView = (path: string, params: Record<string, string>) => `${path}?${new URLSearchParams(params)}`;

/** One domain's page. */
export const domainPath = (domain: string) => `/domains/${encodeURIComponent(domain)}`;
/** Domains list pre-filtered (`show`: ready | none | quarantine | reject | missing | problems | unmapped | unmonitored). */
export const domainsView = (params: Record<string, string>) => `/domains?${new URLSearchParams(params)}`;

/** Orbit for an Autotask company id. */
/** Device Orbit — key `rmm:<uid>` | `intune:<id>` | `itglue:<configId>`. */
export const insightsDevicePath = (key: string) => `/insights/devices/${encodeURIComponent(key)}`;
/** Person Orbit — key = lower-cased email/UPN, or `contact:<id>`. */
export const insightsPersonPath = (key: string) => `/insights/people/${encodeURIComponent(key)}`;
export const insightsFindPath = (q: string) => `/insights/find?q=${encodeURIComponent(q)}`;
export const socIncidentPath = (id: number | string) => `/soc/incidents/${id}`;
/** A SOC list pre-filtered (`show` = one of the page's filter ids), read once then dropped. */
export const socView = (path: string, params: Record<string, string>) => `${path}?${new URLSearchParams(params)}`;
export const dashboardPath = (id: string) => `/dashboards/${id}`;
/** Full-screen wall display (outside the app shell). `cycle` = more dashboard ids to rotate through. */
export const dashboardDisplayPath = (id: string, opts: { cycle?: string[]; every?: number } = {}) => {
  const q = new URLSearchParams();
  if (opts.cycle?.length) q.set('cycle', opts.cycle.join(','));
  if (opts.every) q.set('every', String(opts.every));
  return `/dashboards/${id}/display${q.size ? `?${q}` : ''}`;
};
export const companyPath = (id: number | string) => `/companies/${id}`;
export const docsOrgPath = (id: string, tab?: string) => `/docs/organizations/${id}${tab ? `?tab=${tab}` : ''}`;
/** Docs list pages read `?show=` (and `q`) once, then drop them — same pattern as the other modules. */
export const docsView = (path: string, params: Record<string, string>) => `${path}?${new URLSearchParams(params)}`;
/** Coverage pre-filtered (`state`: gaps | drift | rmm-only | intune-only | duplicates; `tab`: devices | companies), read once then dropped. */
export const insightsCoverageView = (params: Record<string, string>) => `${ROUTES.insights.coverage}?${new URLSearchParams(params)}`;
/** Gap list pre-filtered (`kind`: no-rmm | no-backup | no-intune | drift | leaver | duplicate; `severity`), read once then dropped. */
export const insightsGapsView = (params: Record<string, string>) => `${ROUTES.insights.gaps}?${new URLSearchParams(params)}`;

/** CSP licences pre-filtered by `client` and/or `status` (read once by the page, then dropped). */
export const cspLicencesView = (params: Record<string, string>) => `${ROUTES.csp.licences}?${new URLSearchParams(params)}`;

/**
 * Pre-filtered RMM list views, used by the sidebar Views. The pages read these query
 * params once, apply them over the default filters, then drop them from the URL.
 * Param names are owned by the pages' `filtersFromSearch` (Devices.tsx / Alerts.tsx).
 */
export const rmmDevicesView = (params: Record<string, string>) => `${ROUTES.rmm.devices}?${new URLSearchParams(params)}`;
export const rmmAlertsView = (params: Record<string, string>) => `${ROUTES.rmm.alerts}?${new URLSearchParams(params)}`;

/** Prefixes used for active-state matching in the header and bottom nav. */
export const PREFIX = {
  psaTickets: '/psa/tickets',
  psaOpportunities: '/psa/opportunities',
  psaProjects: '/psa/projects',
  psa: '/psa',
  rmm: '/rmm',
  cipp: '/cipp',
  csp: '/csp',
  backup: '/backup',
  insights: '/insights',
  companies: '/companies',
  network: '/network',
  domains: '/domains',
  docs: '/docs',
  admin: '/admin',
} as const;

/**
 * Pre-`/psa` paths, kept working so muscle memory and any link copied out of an
 * AI-assistant chat still resolves. `:id` entries are forwarded with their id
 * by `LegacyRedirect` in `main.tsx`.
 */
export const LEGACY_REDIRECTS: { from: string; to: string }[] = [
  { from: '/my-tickets',            to: ROUTES.psa.tickets.mine },
  { from: '/tickets',               to: ROUTES.psa.tickets.mine },
  { from: '/dispatch',              to: ROUTES.psa.tickets.queues },
  { from: '/tickets/search',        to: ROUTES.psa.tickets.search },
  { from: '/tickets/create',        to: ROUTES.psa.tickets.new },
  { from: '/my-opportunities',      to: ROUTES.psa.opportunities.mine },
  { from: '/opportunities',         to: ROUTES.psa.opportunities.all },
  { from: '/opportunities/search',  to: ROUTES.psa.opportunities.search },
  { from: '/opportunities/create',  to: ROUTES.psa.opportunities.new },
  { from: '/search',                to: ROUTES.psa.search },
  // Dashboards moved under /psa when the PSA mega menu landed.
  { from: '/dashboards/service-desk', to: ROUTES.psa.dashboards.serviceDesk },
  { from: '/dashboards/sales',        to: ROUTES.psa.dashboards.sales },
];

/** Legacy record paths that carry an id through to the new location. */
export const LEGACY_RECORD_REDIRECTS: { from: string; build: (id: string) => string }[] = [
  { from: '/tickets/:id',       build: ticketPath },
  { from: '/opportunities/:id', build: oppPath },
];
